import {
  LogCategory,
  LogCategoryItem,
  TaskRecurrence,
  TaskType,
} from '@lmc/ts-common'
import { useSelector } from 'react-redux'
import BasicFilters, {
  BasicFiltersProps,
  BasicFiltersValue,
  MultiSelectWide,
} from 'screens/Todos/components/BasicFilters'
import { toDosConstants } from 'locale/toDos'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import styled from 'styled-components'

export type ManagerFiltersValue = BasicFiltersValue & {
  type?: TaskType[]
  frequency?: TaskRecurrence[]
}

export type AllManagerFilters = ManagerFiltersValue & {
  search?: string
}

export type ManagerFiltersProps = BasicFiltersProps<ManagerFiltersValue>
export const getName = (item: { name: { first: string; last: string } }) =>
  `${item.name.first} ${item.name.last}`

const ManagerFilters = ({
  value,
  onChange,
}: ManagerFiltersProps): JSX.Element => {
  const { t } = useTranslation(NAMESPACE.TODOS)
  const handleInputChange = (newValue: Partial<ManagerFiltersValue>) => {
    const updatedValue: ManagerFiltersValue = {
      ...value,
      ...newValue,
    }

    onChange(updatedValue)
  }

  const {
    'log-category-items': categoryItems,
  }: {
    'log-categories': LogCategory[] | undefined
    'log-category-items': LogCategoryItem[] | undefined
  } = useSelector((state: any) => state.data)

  let newCategoryItems = categoryItems?.filter((categoryItem) =>
    value?.categories?.includes(categoryItem.category)
  )

  if (!newCategoryItems?.length) {
    newCategoryItems = categoryItems
  }

  const frequencyOptions: { value: FrequencyValues; label: string }[] = [
    {
      value: TaskRecurrence.daily,
      label: t(toDosConstants[TaskRecurrence.daily]),
    },
    {
      value: TaskRecurrence.weekly,
      label: t(toDosConstants[TaskRecurrence.weekly]),
    },
    {
      value: TaskRecurrence.biWeekly,
      label: t(toDosConstants[TaskRecurrence.biWeekly]),
    },
    {
      value: TaskRecurrence.monthly,
      label: t(toDosConstants[TaskRecurrence.monthly]),
    },
    {
      value: TaskRecurrence.once,
      label: t(toDosConstants[TaskRecurrence.once]),
    },
  ]

  const preferredPeopleNominative: 'Residents' | 'Tenants' = useSelector(
    (state: any) => state.global.home.preferredPeopleNominative
  )

  const typeOptions = [
    {
      value: TaskType.home,
      label: t(toDosConstants[TaskType.home]),
    },
    {
      value: TaskType.resident,
      label: preferredPeopleNominative,
    },
    // on a future update
    // {
    //   value: TaskType.team,
    //   label: t(toDosConstants[TaskType.team]),
    // },
  ]

  const handleFrequencyFilterChange = (values: FrequencyValues[]) => {
    if (values.includes('OTHER')) {
      const other = [
        TaskRecurrence.annually,
        TaskRecurrence.fourWeekly,
        TaskRecurrence.threeWeekly,
      ]

      const newValues = (
        values.filter((value) => value !== 'OTHER') as TaskRecurrence[]
      ).concat(other) as TaskRecurrence[]

      handleInputChange({ frequency: newValues })
    } else {
      handleInputChange({
        frequency: values as TaskRecurrence[],
      })
    }
  }

  return (
    <BasicFilters value={value} onChange={handleInputChange}>
      <StyledMultiSelect160<TaskType>
        title={t(toDosConstants.TYPE)}
        options={typeOptions}
        hasClear
        hasSearch
        hasSelectAll
        onChange={(value) => handleInputChange({ type: value })}
      />
      <StyledMultiSelect176<FrequencyValues>
        title={t(toDosConstants.FREQUENCY)}
        options={frequencyOptions}
        hasClear
        hasSearch
        hasSelectAll
        onChange={handleFrequencyFilterChange}
      />
    </BasicFilters>
  )
}

type FrequencyValues =
  | TaskRecurrence.daily
  | TaskRecurrence.weekly
  | TaskRecurrence.biWeekly
  | TaskRecurrence.monthly
  | TaskRecurrence.once
  | 'OTHER'

const StyledMultiSelect160 = styled(MultiSelectWide)`
  min-width: 160px;
` as typeof MultiSelectWide

const StyledMultiSelect176 = styled(MultiSelectWide)`
  min-width: 176px;
` as typeof MultiSelectWide

export default ManagerFilters
